import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Truck, Leaf, Award, Clock } from "lucide-react";
import { CATEGORIES, TESTIMONIALS } from "../mock";
import { fetchProducts } from "../lib/api";
import ProductCard from "../components/ProductCard";
import ReviewHighlights from "../components/ReviewHighlights";
import FaqSection from "../components/FaqSection";
import FestiveBanner from "../components/FestiveBanner";
import Seo from "../components/Seo";
import { FAQS } from "../data/seoContent";

const Home = () => {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => setProducts([]));
  }, []);
  const allBest = products.filter((p) => p.bestseller);
  const featuredIds = ["GTWHIS1", "GTBEER1", "GTTIRA1"];
  const bestsellers = [
    ...featuredIds.map((id) => allBest.find((p) => p.id === id)).filter(Boolean),
    ...allBest.filter((p) => !featuredIds.includes(p.id)),
  ].slice(0, 8);
  const newArrivals = products.slice(8, 16);

  return (
    <div>
      <Seo
        path="/"
        title="Order Cakes Online in Noida & Greater Noida"
        description="Freshly baked, hand-decorated cakes delivered next-day across Noida, Greater Noida & Indirapuram. Shop birthday, anniversary, designer, photo & custom cakes online at Globaltreats."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
      <FestiveBanner />
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#FCEFE3] to-[#F7DCE4]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 bg-white/70 text-[#6B4226] text-sm px-4 py-1.5 rounded-full mb-5">
              <Star size={14} className="fill-[#D46A8A] text-[#D46A8A]" /> Rated 4.9/5 by 480+ happy customers
            </span>
            <h1 className="font-display text-4xl md:text-6xl font-bold text-[#3A2417] leading-[1.05]">
              Cakes that make<br />every moment <span className="text-[#D46A8A] italic">sweeter</span>
            </h1>
            <p className="text-[#6B4226]/80 text-lg mt-5 max-w-md">
              Freshly baked, hand-decorated masterpieces delivered to your door. Next-day delivery across Noida, Greater Noida & Indirapuram.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/collections/all" className="inline-flex items-center gap-2 h-13 px-7 py-3.5 rounded-full bg-[#D46A8A] text-white font-medium hover:bg-[#c25a7b] transition-colors">
                Order Now <ArrowRight size={18} />
              </Link>
              <Link to="/collections/designer" className="inline-flex items-center h-13 px-7 py-3.5 rounded-full bg-white text-[#6B4226] font-medium hover:bg-[#FFF8F0] transition-colors border border-[#EADBCB]">
                Explore Designer Cakes
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-white/40 rounded-[3rem] blur-xl" />
            <video
              src="/assets/brand-video.mp4"
              className="relative rounded-[2.5rem] bakery-shadow-lg w-full aspect-[4/5] object-cover"
              autoPlay
              muted
              loop
              playsInline
              controls
              poster="https://images.unsplash.com/photo-1621303837174-89787a7d4729?w=800&q=80"
            />
            <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl bakery-shadow px-4 py-3 flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#F6E7DA] grid place-items-center"><Clock size={20} className="text-[#D46A8A]" /></div>
              <div><div className="text-sm font-semibold text-[#3A2417]">Next-day</div><div className="text-xs text-[#B07A5C]">delivery</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-[#6B4226]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-[#FFF8F0]">
          {[
            { icon: Truck, t: "Next-day Delivery", s: "Schedule up to 1 month" },
            { icon: Leaf, t: "100% Eggless Option", s: "On every cake" },
            { icon: Award, t: "Cake Creation By Swati", s: "Hand-decorated" },
            { icon: Star, t: "4.9 Rating", s: "480+ reviews" },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-white/10 grid place-items-center shrink-0"><f.icon size={20} className="text-[#E8A6B8]" /></div>
              <div><div className="text-sm font-semibold">{f.t}</div><div className="text-xs text-[#C9B4A2]">{f.s}</div></div>
            </div>
          ))}
        </div>
      </section>

      <ReviewHighlights />

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#3A2417]">Shop by Occasion</h2>
          <p className="text-[#B07A5C] mt-2">Find the perfect cake for every celebration</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {CATEGORIES.slice(1).map((c) => (
            <Link key={c.slug} to={`/collections/${c.slug}`} className="group relative rounded-2xl overflow-hidden aspect-[4/3] bakery-shadow">
              <img src={c.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3 className="font-display text-lg font-semibold text-white">{c.name}</h3>
                <span className="text-xs text-[#E8A6B8] flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Shop now <ArrowRight size={12} /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Bestsellers */}
      <section className="bg-[#FCEFE3]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[#3A2417]">Our Bestsellers</h2>
              <p className="text-[#B07A5C] mt-2">Loved by thousands, baked fresh daily</p>
            </div>
            <Link to="/collections/all" className="hidden md:inline-flex items-center gap-1 text-[#D46A8A] font-medium hover:gap-2 transition-all">View all <ArrowRight size={18} /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-7">
            {bestsellers.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* Banner */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="relative rounded-[2rem] overflow-hidden bg-[#6B4226]">
          <img src="https://images.unsplash.com/photo-1572978577765-462b91a7f9e1?w=1200&q=80" alt="Custom cakes" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="relative px-8 md:px-16 py-14 md:py-20 text-center max-w-2xl mx-auto">
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white">Dreaming of a custom cake?</h2>
            <p className="text-[#F1E4D6] mt-4">Tell us your theme, flavour and design — our chefs will craft something magical, just for you.</p>
            <Link to="/contact" className="inline-flex items-center gap-2 mt-7 h-13 px-8 py-3.5 rounded-full bg-[#D46A8A] text-white font-medium hover:bg-[#c25a7b] transition-colors">
              Request Custom Cake <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* New arrivals */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-16">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#3A2417]">Fresh Arrivals</h2>
          <Link to="/collections/all" className="hidden md:inline-flex items-center gap-1 text-[#D46A8A] font-medium hover:gap-2 transition-all">View all <ArrowRight size={18} /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-7">
          {newArrivals.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* Video Collage */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-16">
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#3A2417]">Straight From Our Kitchen</h2>
          <p className="text-[#B07A5C] mt-2">Watch our cakes come to life, one delicious creation at a time</p>
        </div>
        <div className="grid grid-cols-2 gap-3 md:gap-5 rounded-[2rem] overflow-hidden">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="relative overflow-hidden rounded-2xl bakery-shadow aspect-square bg-[#F6E7DA]">
              <video
                src={`/assets/collage-${n}.mp4`}
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
              />
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-[#F7DCE4]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#3A2417]">Sweet Words from Our Customers</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.id} className="bg-white rounded-2xl p-6 bakery-shadow">
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: t.rating }).map((_, i) => <Star key={i} size={16} className="fill-[#E8A6B8] text-[#E8A6B8]" />)}
                </div>
                <p className="text-sm text-[#6B4226] leading-relaxed">“{t.text}”</p>
                <div className="flex items-center gap-3 mt-5">
                  <div className="w-10 h-10 rounded-full bg-[#D46A8A] text-white grid place-items-center font-semibold">{t.avatar}</div>
                  <div><div className="text-sm font-semibold text-[#3A2417]">{t.name}</div><div className="text-xs text-[#B07A5C]">{t.city}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <FaqSection />
    </div>
  );
};

export default Home;
